import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { GitBranch, Plus, Trash2, ArrowRight, Loader2, X, Clock } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];

function relTime(iso, es) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return es ? "hace instantes" : "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return es ? `hace ${m} min` : `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return es ? `hace ${h} h` : `${h}h ago`;
  return new Date(iso).toLocaleDateString(es ? "es-MX" : "en-US");
}

function CreateFlowDialog({ onClose, onCreated }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [statesStr, setStatesStr] = useState("");
  const [initial, setInitial] = useState("");
  const [transitions, setTransitions] = useState([{ from: "", to: "", label: "", allowed_roles: [] }]);
  const [saving, setSaving] = useState(false);
  const states = statesStr.split(",").map((s) => s.trim()).filter(Boolean);

  const submit = async () => {
    if (!name.trim() || states.length === 0) return toast.error(t("os.workflows.nameRequired"));
    setSaving(true);
    try {
      const trans = transitions.filter((tr) => tr.from && tr.to)
        .map((tr) => ({ from: tr.from, to: tr.to, label: tr.label, allowed_roles: tr.allowed_roles || [] }));
      await api.post("/workflows", {
        name, description, states, initial_state: initial || states[0], transitions: trans,
      });
      toast.success(t("os.workflows.created"));
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wf-create-dialog">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className="card-surface w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{t("os.workflows.newFlow")}</h3>
          <button onClick={onClose} data-testid="wf-create-close" className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input className="field" placeholder={t("os.workflows.name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="wf-name" />
          <input className="field" placeholder={t("os.workflows.description")} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="wf-desc" />
          <input className="field" placeholder={t("os.workflows.states")} value={statesStr} onChange={(e) => setStatesStr(e.target.value)} data-testid="wf-states" />
          <select className="field" value={initial} onChange={(e) => setInitial(e.target.value)} data-testid="wf-initial">
            <option value="">{t("os.workflows.initial")}</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="pt-1">
            <div className="text-sm font-medium mb-1.5">{t("os.workflows.transitions")}</div>
            {transitions.map((tr, i) => (
              <div key={i} className="flex gap-1.5 mb-1.5">
                <select className="field-sm flex-1" value={tr.from} onChange={(e) => { const n = [...transitions]; n[i].from = e.target.value; setTransitions(n); }} data-testid={`wf-tr-from-${i}`}>
                  <option value="">{t("os.workflows.from")}</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="field-sm flex-1" value={tr.to} onChange={(e) => { const n = [...transitions]; n[i].to = e.target.value; setTransitions(n); }} data-testid={`wf-tr-to-${i}`}>
                  <option value="">{t("os.workflows.to")}</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className="field-sm flex-1" placeholder={t("os.workflows.label")} value={tr.label} onChange={(e) => { const n = [...transitions]; n[i].label = e.target.value; setTransitions(n); }} />
              </div>
            ))}
            <button onClick={() => setTransitions([...transitions, { from: "", to: "", label: "", allowed_roles: [] }])}
              className="text-sm text-violet inline-flex items-center gap-1" data-testid="wf-add-transition">
              <Plus className="w-3.5 h-3.5" /> {t("os.workflows.addTransition")}
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn btn-ghost" data-testid="wf-cancel">{t("os.workflows.cancel")}</button>
          <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="wf-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.workflows.create")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function InstanceCard({ inst, def, onTransition, es, t }) {
  const [to, setTo] = useState("");
  const options = (def?.transitions || []).filter((tr) => tr.from === inst.current_state);
  return (
    <div className="card-surface p-4" data-testid={`wf-instance-${inst.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{inst.title || inst.resource_id}</div>
          <div className="text-xs text-navy/50 mt-0.5">{inst.definition_name} · <span className="font-mono">{inst.resource_type}</span></div>
        </div>
        <span className="badge badge-violet shrink-0" data-testid={`wf-state-${inst.id}`}>{inst.current_state}</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-navy/40">
        <Clock className="w-3 h-3" /> {t("os.workflows.startedAt")} {relTime(inst.created_at, es)} · {inst.history?.length || 0} {t("os.workflows.history")}
      </div>
      {options.length > 0 ? (
        <div className="mt-3 flex gap-2">
          <select className="field-sm flex-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`wf-move-${inst.id}`}>
            <option value="">{t("os.workflows.transitionTo")}</option>
            {options.map((tr) => <option key={tr.to} value={tr.to}>{tr.label || tr.to}</option>)}
          </select>
          <button disabled={!to} onClick={() => { onTransition(inst.id, to); setTo(""); }}
            className="btn btn-outline text-sm" data-testid={`wf-apply-${inst.id}`}>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-navy/40">{t("os.workflows.noTransitions")}</div>
      )}
    </div>
  );
}

export default function Workflows() {
  const { t, lang } = useI18n();
  const es = lang === "es";
  const [tab, setTab] = useState("instances");
  const [defs, setDefs] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, i] = await Promise.all([api.get("/workflows"), api.get("/workflows/instances")]);
      setDefs(d.data); setInstances(i.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const defById = Object.fromEntries(defs.map((d) => [d.id, d]));

  const transition = async (iid, to) => {
    try {
      await api.post(`/workflows/instances/${iid}/transition`, { to });
      toast.success(t("os.workflows.transitioned"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (did) => {
    try { await api.delete(`/workflows/${did}`); toast.success(t("os.workflows.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <OSLayout title={es ? "Flujos" : "Workflows"}>
      <div className="space-y-8" data-testid="workflows-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2 flex items-center gap-2"><GitBranch className="w-3.5 h-3.5" /> {t("os.workflows.overline")}</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.workflows.title")}</h2>
            <p className="mt-2 text-navy/50 max-w-2xl">{t("os.workflows.subtitle")}</p>
          </motion.div>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary" data-testid="wf-new-btn">
            <Plus className="w-4 h-4" /> {t("os.workflows.newFlow")}
          </button>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setTab("instances")} data-testid="wf-tab-instances"
            className={`btn text-sm ${tab === "instances" ? "btn-primary" : "btn-ghost"}`}>{t("os.workflows.tabInstances")}</button>
          <button onClick={() => setTab("defs")} data-testid="wf-tab-defs"
            className={`btn text-sm ${tab === "defs" ? "btn-primary" : "btn-ghost"}`}>{t("os.workflows.tabDefs")}</button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : tab === "instances" ? (
          instances.length === 0 ? (
            <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="wf-no-instances">{t("os.workflows.noInstances")}</div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wf-instances-grid">
              {instances.map((inst) => (
                <InstanceCard key={inst.id} inst={inst} def={defById[inst.definition_id]} onTransition={transition} es={es} t={t} />
              ))}
            </div>
          )
        ) : (
          defs.length === 0 ? (
            <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="wf-no-defs">{t("os.workflows.noDefs")}</div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wf-defs-grid">
              {defs.map((d) => (
                <div key={d.id} className="card-surface p-4" data-testid={`wf-def-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{d.name}</div>
                    <button onClick={() => del(d.id)} className="text-navy/30 hover:text-destructive" data-testid={`wf-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {d.description && <p className="text-xs text-navy/50 mt-1">{d.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {d.states.map((s) => (
                      <span key={s} className={`badge ${s === d.initial_state ? "badge-emerald" : "badge-muted"}`}>{s}</span>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-navy/40">{d.states.length} {t("os.workflows.statesLabel")} · {d.transitions.length} {t("os.workflows.transLabel")}</div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {showCreate && <CreateFlowDialog onClose={() => setShowCreate(false)} onCreated={load} />}
    </OSLayout>
  );
}
